import type { StorageItem, StorageResourceRef } from '../../../../types/azureStorage';

export type PendingStorageFolder = {
  container: string;
  prefix: string;
  createdAt: number;
};

/**
 * A folder name, or a relative path of folder names (UI/UX fix round A, review U38: "2026/10" creates 2026 and 2026/10,
 * as the Data Lake view's New folder does — a virtual folder path is a valid blob-name prefix). One trailing "/" is
 * ignored; empty parts, "." / ".." parts, backslashes and control characters are refused.
 */
export function validatePendingFolderName(name: string): string | undefined {
  const value = name.trim().replace(/\/$/u, '');
  if (!value) return 'Folder name is required.';
  if (value.includes('\\')) return 'Folder name cannot contain a backslash; use / between folders.';
  const segments = value.split('/');
  if (segments.some((segment) => segment === '')) return 'A folder path cannot start with / or contain //.';
  if (segments.some((segment) => segment === '.' || segment === '..')) return 'Folder names cannot be a dot segment (. or ..).';
  if (/\p{Cc}/u.test(value)) return 'Folder name cannot contain control characters.';
  return undefined;
}

/** One pending folder per level of `name` under `parentPrefix` (a single name gives one). */
export function createPendingFolders(args: {
  container: string;
  parentPrefix: string;
  name: string;
  now?: number;
}): PendingStorageFolder[] {
  const error = validatePendingFolderName(args.name);
  if (error) throw new Error(error);
  const segments = args.name.trim().replace(/\/$/u, '').split('/');
  const parentPrefix = args.parentPrefix && !args.parentPrefix.endsWith('/')
    ? `${args.parentPrefix}/`
    : args.parentPrefix;
  const createdAt = args.now ?? Date.now();
  return segments.map((_segment, index) => ({
    container: args.container,
    prefix: `${parentPrefix}${segments.slice(0, index + 1).join('/')}/`,
    createdAt,
  }));
}

function parentPrefixOf(prefix: string): string {
  const withoutSlash = prefix.endsWith('/') ? prefix.slice(0, -1) : prefix;
  const separator = withoutSlash.lastIndexOf('/');
  return separator < 0 ? '' : `${withoutSlash.slice(0, separator + 1)}`;
}

export function pendingFoldersForLocation(
  folders: readonly PendingStorageFolder[],
  resource: StorageResourceRef,
): PendingStorageFolder[] {
  if (resource.kind !== 'container' && resource.kind !== 'blobPrefix') return [];
  const prefix = resource.kind === 'blobPrefix' ? resource.prefix : '';
  return folders.filter((folder) => folder.container === resource.container && parentPrefixOf(folder.prefix) === prefix);
}

export function reconcilePendingFolders(
  folders: readonly PendingStorageFolder[],
  items: readonly StorageItem[],
): PendingStorageFolder[] {
  return folders.filter((folder) => !items.some((item) =>
    (item.kind === 'folder' && item.container === folder.container && item.prefix === folder.prefix)
    || (item.kind === 'blob' && item.container === folder.container && item.name.startsWith(folder.prefix)),
  ));
}
